export const recipesEn = {
  title: 'Recipes and bills of materials',
  subtitle: 'Versioned, auditable formulations and multi-level BOMs.',
  empty: 'No recipe version matches the selected filters.',
  pagination: 'Page {{page}} of {{pages}} · {{total}} versions',
  help: {
    open: 'Open help for recipes and BOMs',
    list: 'Filter recipes by lifecycle status, output product, component or validity.',
    header:
      'The number and output product identify the stable recipe. Content is changed through controlled versions.',
    translations: 'Names and descriptions are stored separately for every supported language.',
    components:
      'Positions may reference products or approved sub-recipes. Quantities and costs use exact decimals.',
    dmsSnapshot: 'Select the protected DMS originals that belong to this exact version snapshot.',
    history: 'Released versions remain immutable and can be copied into a new draft.',
    impact:
      'Shows direct parent recipes and all affected output products across the complete BOM hierarchy.',
    productUsage: 'Select a component to show every recipe and product that uses it directly.',
  },
  actions: {
    new: 'New recipe / BOM',
    alternatives: 'Alternatives',
    submit: 'Submit for review',
    review: 'Complete review',
    approve: 'Approve',
    copy: 'Copy version',
    block: 'Block',
    archive: 'Archive',
    addComponent: 'Add position',
    compare: 'Compare versions',
    addAlternative: 'Add alternative',
    reject: 'Reject',
  },
  filters: {
    search: 'Search number, recipe or output product',
    status: 'Lifecycle status',
    allStatuses: 'All statuses',
    validOn: 'Valid on',
    componentSearch: 'Search positions by article, recipe, position or comment',
  },
  columns: {
    number: 'Number',
    name: 'Name',
    output: 'Output product',
    version: 'Version',
    status: 'Status',
    reference: 'Reference quantity',
    components: 'Positions',
    cost: 'Material cost',
    validity: 'Validity',
    updated: 'Last change',
  },
  statuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    BLOCKED: 'Blocked',
    ARCHIVED: 'Archived',
  },
  componentTypes: {
    MATERIAL: 'Material',
    SUB_RECIPE: 'Sub-recipe',
    PACKAGING: 'Packaging',
    BY_PRODUCT: 'By-product',
  },
  substitutionStatuses: {
    PROPOSED: 'Proposed',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ARCHIVED: 'Archived',
  },
  editor: {
    newTitle: 'Create recipe / BOM',
    newDescription: 'Create the stable identity and its first editable version.',
    versionDescription: 'Controlled version v{{version}}',
    reorder: 'Reorder position',
  },
  fields: {
    number: 'Recipe / BOM number',
    outputProduct: 'Output product',
    name: 'Name',
    baseQuantity: 'Reference quantity',
    baseUnit: 'Base unit',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    currency: 'Cost currency',
    comments: 'Internal comments',
    localizedName: 'Localized name',
    description: 'Description',
    position: 'Position',
    type: 'Position type',
    component: 'Component',
    quantity: 'Quantity',
    unit: 'Unit',
    percentage: 'Share %',
    loss: 'Loss %',
    declarationRelevant: 'Declaration-relevant',
    alternatives: 'Alternatives',
    comment: 'Comment',
    selectSubRecipe: 'Select approved sub-recipe',
    selectProduct: 'Select product',
    alternativeProduct: 'Alternative product',
    alternativeRecipe: 'Alternative sub-recipe',
    factor: 'Substitution factor',
    rationale: 'Technical rationale',
    reason: 'Reason and decision note',
  },
  sections: {
    header: 'Recipe header',
    translations: 'Languages',
    components: 'Components and sub-recipes',
    snapshotDocuments: 'Version document snapshot',
    history: 'Version history',
    whereUsed: 'Parent recipes and affected products',
    affectedProducts: 'All affected products across BOM levels',
    productUsage: 'Usage of the selected component',
    documents: 'Recipe DMS record',
  },
  summary: {
    quantities: 'Position totals by unit',
    percentage: 'Maintained share total',
    cost: 'Calculated material cost',
  },
  warnings: {
    UNIT_NOT_SUMMABLE: 'Position {{position}} cannot be converted to the base unit {{unit}}.',
    COMPONENT_ARCHIVED: 'The product in position {{position}} is archived.',
    SUB_RECIPE_NOT_APPROVED: 'The sub-recipe in position {{position}} is not approved.',
    PERCENTAGE_TOTAL_NOT_100: 'The maintained shares total {{percentage}}% instead of 100%.',
    INPUT_OUTPUT_DEVIATION:
      'Convertible inputs ({{input}} {{unit}}) differ from the reference output ({{output}} {{unit}}).',
  },
  states: {
    invalidDecimal: 'Invalid decimal',
    noDocuments: 'No protected recipe documents are available.',
    notUsed: 'No usage found.',
  },
  messages: {
    saved: 'Recipe version saved.',
    invalid: 'Please correct the invalid recipe data.',
    submitted: 'Recipe submitted for independent review.',
    workflowUpdated: 'Recipe workflow updated.',
  },
  tabs: {
    editor: 'Editor',
    history: 'Versions and comparison',
    impact: 'Where used / impact',
    processRouting: 'Process routing',
    documents: 'DMS documents',
  },
  alternatives: {
    title: 'Alternative components and approved substitutions',
    empty: 'No alternative has been defined for this position.',
  },
  actionDialog: {
    REVIEW: {
      title: 'Complete technical review',
      description: 'Confirm the independent review with a traceable note.',
    },
    APPROVE: {
      title: 'Approve recipe version',
      description: 'Approval freezes this version and requires four-eyes separation.',
    },
    COPY: {
      title: 'Copy into a new version',
      description: 'A new editable draft is created; substitution decisions are reviewed again.',
    },
    BLOCK: {
      title: 'Block recipe version',
      description: 'The released or reviewed version remains immutable but can no longer be used.',
    },
    ARCHIVE: {
      title: 'Archive recipe version',
      description: 'The version remains fully traceable and is removed from active use.',
    },
    SUB_APPROVE: {
      title: 'Approve substitution',
      description: 'Confirm technical equivalence independently and document the evidence.',
    },
    SUB_REJECT: {
      title: 'Reject substitution',
      description: 'Document why this proposed alternative must not be used.',
    },
  },
  compare: {
    title: 'Compare recipe versions',
    description: 'Header and position differences are derived from immutable version snapshots.',
    headerChanges: 'Header changes',
    none: 'No header changes.',
    added: 'Added positions',
    removed: 'Removed positions',
    changed: 'Changed positions',
  },
  compareFields: {
    name: 'Name',
    baseQuantity: 'Reference quantity',
    baseUnitCode: 'Base unit',
    validFrom: 'Valid from',
    validTo: 'Valid until',
    comments: 'Comments',
    dmsAttachmentIds: 'DMS document snapshot',
    costCurrency: 'Cost currency',
    internalMaterialCost: 'Material cost',
    translations: 'Translations',
  },
} as const;
